import _ from 'lodash';
import moment from 'moment';
import { individualSeatingDetailLabel } from '../utils/format';
import { formatSeminarRoomEquipmentSentence, getSeminarRoomDisplayHeader, isSeminarRoomFacility, replaceSeminarRoomEquipmentSentence } from './seminarRoom';

export { getSeminarRoomDisplayHeader, isSeminarRoomFacility, isSeminarRoomFacilityLookupCode } from './seminarRoom';
export type { SeminarRoomFacilityLike } from './seminarRoom';
import {
  EContent_HotelServiceTypeAssignment,
  EPricing_FacilityLookupCode,
  EPricing_FacilityPayment,
  EPricing_ProductLookupCode,
  EPricing_ProductPayment,
  ICalcLineItem,
  ICalcOptions,
  ICalcPriceOutput,
  IFacility,
  IProduct,
  IProductBundle,
  IQuickPriceDay,
  IQuickPriceInput,
  IServiceTypeFacility,
  IServiceTypeProduct,
  mergeLineItem,
  mergeTaxes,
  NO_PRICE,
  QuickPriceCalculatorBase,
  QuickPriceError,
  sumTotalPrices,
} from './quickprice';

type CatalogRef = { id: number; sku?: string; lookupCode?: string | null; name?: string | null };

export const resolveListerCatalogProduct = (ref: CatalogRef | null | undefined, catalog: IProduct[]): IProduct | undefined => {
  if (!ref) return undefined;
  return (
    catalog.find(p => p.id === ref.id) ||
    (ref.sku ? catalog.find(p => p.sku === ref.sku) : undefined) ||
    (ref.lookupCode ? catalog.find(p => p.lookupCode === ref.lookupCode) : undefined)
  );
};

export const resolveListerCatalogBundle = (
  ref: CatalogRef | null | undefined,
  catalog: IProductBundle[],
  assignment?: string | null,
): IProductBundle | undefined => {
  if (!ref) return undefined;
  const byId = catalog.find(b => b.id === ref.id);
  if (byId) return byId;
  if (ref.sku) {
    const bySku = catalog.find(b => b.sku === ref.sku);
    if (bySku) return bySku;
  }
  if (!ref.lookupCode) return undefined;
  const byLookup = catalog.filter(b => b.lookupCode === ref.lookupCode);
  if (byLookup.length === 0) return undefined;
  if (byLookup.length === 1) return byLookup[0];

  const hints: Record<string, string[]> = {
    [EContent_HotelServiceTypeAssignment.REGULAR]: ['Ganztags', '00001'],
    [EContent_HotelServiceTypeAssignment.MEDIUM]: ['Halbtags', '00002'],
    [EContent_HotelServiceTypeAssignment.SMALL]: ['Klein', '00003'],
  };
  const hintList = (assignment && hints[assignment]) || [];
  return byLookup.find(b => hintList.some(h => (b.name || '').includes(h) || (b.sku || '').includes(h))) || byLookup[0];
};

export interface IQuickPriceInputLister extends IQuickPriceInput<IQuickPriceDayLister> {
  includeMainRoomInPackage?: boolean | null;
}

export interface IQuickPriceDayLister extends IQuickPriceDay {
  occupancy: {
    from: string;
    to: string;
    requireAirConditioning?: boolean | null;
    requireDaylight?: boolean | null;
    beamerCount?: number | null;
    flipchartCount?: number | null;
    pinboardCount?: number | null;
  }[];
  overnightRoomsEZ: number;
  overnightRoomsDZ: number;
  serviceTypes: IServiceTypeConfiguration[];
  addons: IQuickPriceDayAddonLister[];
  seatCount?: number[];
  /** Parallel to `seating`. Free text when the matching seating value is OTHER. */
  seatingDescription?: Array<string | null> | null;
}

export interface IServiceTypeConfiguration {
  serviceTypeSku: string;
  guestsCount: number;
  /** Lister assignment (SMALL/MEDIUM/REGULAR). Needed when multiple assignments share one SKU. */
  assignment?: string;
}

export interface IQuickPriceDayAddonLister {
  sku: string;
  count: number;
}

export type QuickPriceInputTypeLister = IQuickPriceInputLister;
export type CalcPriceOutputTypeLister = ICalcPriceOutput;
export type CalcLineItemTypeLister = ICalcLineItem;
export type QuickPriceCalculatorTypeLister = QuickPriceCalculatorLister;

/**
 * Classifies an occupancy block as full-day when duration exceeds four hours.
 */
export const isOccupancyFullDay = (occ: { from: string; to: string }) => {
  try {
    const froms = occ.from ? occ.from.split(':', 2).map(p => parseInt(p)) : [8, 0];
    const tos = occ.to ? occ.to.split(':', 2).map(p => parseInt(p)) : [18, 0];

    //more than four hours counts as full day
    return tos[0] - froms[0] > 4;
  } catch (err) {
    console.warn('isOccupancyFullDay failed to get occupancy', err);
    return true;
  }
};

/**
 * Filters line items to those relevant for the lister day view.
 */
export const filterListerVisibleLineItems = (lineItems: ICalcLineItem[], day: number) => {
  const filtered = lineItems.filter(li => {
    if (li.day !== day) return false;
    if (!li.isIncluded || li.priceItem > 0) return true;
    if (li.facility && li.facility.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_FULLDAY) return true;
    if (li.facility && li.facility.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_SEMIDAY) return true;
    return false;
  });

  return filtered;
};

/**
 * Maps seating type to meeting room capacity field
 */
export const getCapacityFieldForSeating = (seating: string): keyof NonNullable<IFacility['meetingRoom']> | null => {
  switch (seating) {
    case 'UFORM':
      return 'capacityUForm';
    case 'THEATER':
      return 'capacityTheater';
    case 'PARLAMENT':
      return 'capacityParlament';
    case 'CIRCLE':
      return 'capacityCircle';
    case 'BANKETT':
      return 'capacityBankett';
    case 'COCKTAIL':
      return 'capacityCocktail';
    case 'BLOCK':
      return 'capacityBlock';
    default:
      return null;
  }
};

/**
 * Selects the smallest meeting room that can accommodate the required guests and seating
 */
export const selectOptimalMeetingRoom = (facilities: IFacility[], requiredGuests: number, seating: string): IFacility | null => {
  const capacityField = getCapacityFieldForSeating(seating);

  const suitableRooms = capacityField
    ? facilities
        .filter(f => f.meetingRoom && f.meetingRoom[capacityField])
        .map(f => ({
          facility: f,
          capacity: Number(f.meetingRoom![capacityField]) || 0,
          area: f.meetingRoom!.area || 0,
        }))
        .filter(r => r.capacity >= requiredGuests)
    : [];

  if (suitableRooms.length > 0) {
    // Sort by capacity (ascending) then by area (ascending) to get the smallest suitable room
    suitableRooms.sort((a, b) => a.capacity - b.capacity || a.area - b.area);
    return suitableRooms[0].facility;
  }

  // Fallback 1: Try rooms with the specific capacity field but smaller capacity
  const roomsWithCapacityField = capacityField
    ? facilities
        .filter(f => f.meetingRoom && f.meetingRoom[capacityField])
        .map(f => ({
          facility: f,
          capacity: Number(f.meetingRoom![capacityField]) || 0,
          area: f.meetingRoom!.area || 0,
        }))
        .sort((a, b) => b.capacity - a.capacity || b.area - a.area) // Largest first
    : [];

  if (roomsWithCapacityField.length > 0) {
    return roomsWithCapacityField[0].facility;
  }

  // OTHER has no capacity column. Any meeting room still produces a visible seminar-room line.
  const anyRoomWithMeetingRoom = facilities
    .filter(f => f.meetingRoom)
    .map(f => ({
      facility: f,
      area: f.meetingRoom!.area || 0,
    }))
    .sort((a, b) => b.area - a.area); // Largest area first

  if (anyRoomWithMeetingRoom.length > 0) {
    return anyRoomWithMeetingRoom[0].facility;
  }

  return null;
};

/**
 * Copies Other seating text onto seminar-room line items so hotels see it on the offer.
 * Rooms are matched in order. Extra descriptions are appended to the last room line.
 */
const attachOtherSeatingDescriptions = (result: ICalcPriceOutput, input: IQuickPriceInputLister) => {
  for (const day of input.days || []) {
    if (day.day < 0) continue;
    const otherRooms = (day.seating || [])
      .map((seating, index) => ({
        seating,
        text: (day.seatingDescription?.[index] || '').trim(),
        seatCount: day.seatCount?.[index],
      }))
      .filter(room => room.seating === 'OTHER' && room.text);
    if (otherRooms.length === 0) continue;

    const seminarItems = result.lineItems.filter(
      lineItem => lineItem.day === day.day && lineItem.facility && isSeminarRoomFacility(lineItem.facility),
    );
    if (seminarItems.length === 0) continue;

    otherRooms.forEach((room, index) => {
      const target = seminarItems[Math.min(index, seminarItems.length - 1)];
      if (!target.seatingType || target.seatingType === 'OTHER') {
        target.seatingType = 'OTHER';
        if (target.seatCount == null && room.seatCount != null) target.seatCount = room.seatCount;
      }
      target.seatingDescription = target.seatingDescription ? `${target.seatingDescription}\n\n${room.text}` : room.text;
    });
  }
};

export class QuickPriceCalculatorLister extends QuickPriceCalculatorBase<
  IQuickPriceDayLister,
  IQuickPriceInputLister,
  ICalcOptions,
  ICalcPriceOutput
> {
  public getSKUIncludedInDayServiceTypes(qi: IQuickPriceInputLister, day: number, addons: boolean = false) {
    const serviceTypesConfigs = this._getDayServiceTypeConfigs(qi, day);

    const serviceTypeIncludedSkus = _.uniq(
      serviceTypesConfigs.services.flatMap(stc => this.getSKUIncludedInServiceType(stc.serviceType.sku, addons)),
    );
    return serviceTypeIncludedSkus;
  }

  private _getDayServiceTypeConfigs = (input: IQuickPriceInputLister, day: number) => {
    const dayConfig = input.days.find(d => d.day === day);
    if (dayConfig) {
      const serviceTypeGuests = dayConfig.serviceTypes.reduce((agg, st) => agg + st.guestsCount, 0);
      const roomGuests = dayConfig.overnightRoomsEZ + dayConfig.overnightRoomsDZ;
      return {
        // Falls back to room occupancy so days without a seminar service type (e.g. arrival/Vortag)
        // still match guest-count-based price rules instead of defaulting to 0 guests.
        totalGuests: Math.max(serviceTypeGuests, roomGuests),
        roomsCountEZ: dayConfig.overnightRoomsEZ,
        roomsCountDZ: dayConfig.overnightRoomsDZ,
        services: (this.allServiceTypesDb || []).map(serviceType => {
          // Match by assignment when present — same Pricing_ServiceType SKU can be linked to
          // both SMALL and REGULAR on a hotel, and SKU-only find() would duplicate guest counts.
          const ist = dayConfig.serviceTypes.find(p => {
            if (p.assignment && serviceType.listerServiceTypeAssignment) {
              return p.assignment === serviceType.listerServiceTypeAssignment;
            }
            if (p.assignment && !serviceType.listerServiceTypeAssignment) {
              return false;
            }
            return p.serviceTypeSku === serviceType.sku;
          });
          return { serviceTypeSku: serviceType.sku, guestsCount: ist?.guestsCount || 0, serviceType };
        }),
      };
    } else
      return {
        totalGuests: 0,
        roomsCountEZ: 0,
        roomsCountDZ: 0,
        services: (this.allServiceTypesDb || []).map(serviceType => {
          return { serviceTypeSku: serviceType.sku, guestsCount: 0, serviceType };
        }),
      };
  };

  public calculatePrice(input: IQuickPriceInputLister, calcOptions?: ICalcOptions): ICalcPriceOutput {
    const startMom = moment(input.startDate);
    const endMom = moment(input.endDate);

    const days = endMom.diff(startMom, 'days') + 1;
    for (let dayNum = -1; dayNum < days; dayNum++) {
      if (input.days.findIndex(d => d.day === dayNum) < 0) {
        throw new QuickPriceError(`No selection found for day ${dayNum}`, {
          extensions: { code: 'BAD_INPUT' },
        });
      }
    }

    const initialPriceList = this.getPriceList({
      date: startMom.toDate(),
      isPremium: input.isPremium,
    });

    const allProductsDb = this.allProductsDb!;
    const allFacilitiesDb = this.allFacilitiesDb!;
    const allBundlesDb = this.allBundlesDb!;

    const addonsOnly = !(calcOptions?.addonsOnly === false || calcOptions?.addonsOnly === undefined);

    // TODO: Validate min/max visitor constraints for all selected service types.

    const result: ICalcPriceOutput = {
      priceList: initialPriceList,
      hotel: this.hotel!,
      totalPriceGross: 0.0,
      totalPriceNet: 0.0,
      totalTaxes: 0.0,
      taxes: [],
      lineItems: [],
    };

    // Track which days already have a main room added (for includeMainRoomInPackage feature)
    const mainRoomAddedPerDay: Record<number, boolean> = {};

    // Use includeMainRoomInPackage from input if provided, otherwise fall back to calculator's setting
    const includeMainRoomInPackage = input.includeMainRoomInPackage !== undefined ? input.includeMainRoomInPackage : this.includeMainRoomInPackage;

    const _isFullDay = (day?: IQuickPriceDayLister) =>
      day === undefined || day.occupancy.length === 0 || day.occupancy.findIndex(o => isOccupancyFullDay(o)) >= 0;
    const _getForCode = <TItem extends { bundle?: IProductBundle; product?: IProduct; facility?: IFacility }>(items: TItem[], lookupCode: string) =>
      items.find(item =>
        item.bundle
          ? item.bundle.lookupCode === lookupCode
          : item.product
            ? item.product.lookupCode === lookupCode
            : item.facility
              ? item.facility.lookupCode === lookupCode
              : false,
      );
    const _getAddonForCode = (lookupCode: string) => {
      for (const st of this.allServiceTypesDb || []) {
        const addonFacility = st.addons.find(a => {
          if (!a.facility) return false;
          const f = allFacilitiesDb.find(ff => ff.id === a.facility!.id);
          if (!f) return false;
          const isSeminar =
            f.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_FULLDAY || f.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_SEMIDAY;
          if (f.lookupCode !== lookupCode) return false;
          if (isSeminar) return !!f.meetingRoom && f.meetingRoom.hotelId === this.hotel!.id;
          return true;
        });
        if (addonFacility) return addonFacility;

        const addonProduct = st.addons.find(a => a.product && allProductsDb.find(p => p.id === a.product!.id)?.lookupCode === lookupCode);
        if (addonProduct) return addonProduct;
      }
      return null;
    };

    const _filterServiceTypeProductsByPayment = (
      services: ReturnType<QuickPriceCalculatorLister['_getDayServiceTypeConfigs']>['services'],
      recurring?: string,
    ) => {
      const productConfigs: {
        products: (Omit<IServiceTypeProduct, 'product' | 'bundle'> & { product?: IProduct; bundle?: IProductBundle })[];
        guestsCount: number;
      }[] = [];

      // Only include service types with guests > 0 to avoid picking wrong products for remaining items
      for (const st of services.filter(s => s.guestsCount > 0)) {
        const assignment = st.serviceType.listerServiceTypeAssignment;
        const products = st.serviceType.products
          .map(sp => {
            if (sp.product) {
              const p = resolveListerCatalogProduct(sp.product as CatalogRef, allProductsDb);
              return { ...sp, product: p, bundle: undefined };
            }
            if (sp.bundle) {
              const b = resolveListerCatalogBundle(sp.bundle as CatalogRef, allBundlesDb, assignment);
              return { ...sp, product: undefined, bundle: b };
            }
          })
          .filter(sp => {
            if (sp && sp.product && (recurring ? sp.product.recurring === recurring : true)) return true;
            if (sp && sp.bundle && (recurring ? sp.bundle.recurring === recurring : true)) return true;
          })
          .map(sp => sp!);
        productConfigs.push({ products, guestsCount: st.guestsCount });
      }
      return productConfigs
        .flatMap(pc => pc.products.map(p => ({ ...p, recurring, guestsCount: pc.guestsCount })))
        .reduce(
          (acc, p) => {
            const existing = acc.find(p2 => (p.product ? p.product.id === p2.product?.id : p.bundle ? p.bundle.id === p2.bundle?.id : false));
            if (existing) existing.guestsCount += p.guestsCount;
            else acc.push(p);
            return acc;
          },
          [] as (Omit<IServiceTypeProduct, 'product' | 'bundle'> & {
            product?: IProduct;
            bundle?: IProductBundle;
            recurring?: string;
            guestsCount: number;
          })[],
        );
    };
    const _filterServiceTypeFacilitiesByPayment = (
      services: ReturnType<QuickPriceCalculatorLister['_getDayServiceTypeConfigs']>['services'],
      recurring?: string,
    ) => {
      const facilityConfigs: {
        facilities: (Omit<IServiceTypeFacility, 'facility'> & { facility: IFacility })[];
        guestsCount: number;
      }[] = [];

      for (const st of services.filter(st => st.guestsCount > 0)) {
        const facilities = st.serviceType.facilities
          .map(sf => {
            const f = allFacilitiesDb.find(fd => fd.id === sf.facility.id)!;
            return { ...sf, facility: f };
          })
          .filter(sf => {
            if (!sf.facility) return false;
            if (recurring && sf.facility.recurring !== recurring) return false;
            const isSeminar =
              sf.facility.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_FULLDAY ||
              sf.facility.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_SEMIDAY;
            if (isSeminar) return !!sf.facility.meetingRoom && sf.facility.meetingRoom.hotelId === this.hotel!.id;
            return true;
          });
        facilityConfigs.push({ facilities, guestsCount: st.guestsCount });
      }

      return facilityConfigs
        .flatMap(fc => fc.facilities.map(p => ({ ...p, recurring, guestsCount: fc.guestsCount })))
        .reduce(
          (acc, p) => {
            const existing = acc.find(p2 => p2.facility.id === p.facility.id);
            if (existing) existing.guestsCount += p.guestsCount;
            else acc.push(p);
            return acc;
          },
          [] as (Omit<IServiceTypeFacility, 'facility'> & { facility: IFacility; recurring?: string; guestsCount: number })[],
        );
    };

    for (let dayNum = -1; dayNum < days; dayNum++) {
      const dayMom = moment(startMom).add(dayNum, 'days');
      const day = input.days.find(d => d.day === dayNum);

      const dayServiceTypes = this._getDayServiceTypeConfigs(input, dayNum);
      const priceList = this.getPriceList({ date: dayMom.toDate(), isPremium: input.isPremium });

      const isFullDay = _isFullDay(day);
      const isPrevDay = dayNum === -1;
      const isDepartureDay = days > 1 && dayNum === days - 1;
      const isSeminarDay = !isPrevDay && !isDepartureDay;

      // Calculate Vollpension combinations based on participant counts
      // Only for seminar days with full day occupancy
      // And only for service types assigned as REGULAR (for lister)
      // Lister-specific VP logic only applies to REGULAR-assigned service types.
      const regularServices = dayServiceTypes.services.filter(
        s =>
          (s.serviceType.listerServiceTypeAssignment || EContent_HotelServiceTypeAssignment.REGULAR) === EContent_HotelServiceTypeAssignment.REGULAR,
      );
      const nonRegularServices = dayServiceTypes.services.filter(
        s =>
          (s.serviceType.listerServiceTypeAssignment || EContent_HotelServiceTypeAssignment.REGULAR) !== EContent_HotelServiceTypeAssignment.REGULAR,
      );

      // Keep full product sets to support fallback matching across service types.
      const recurringProductsAll = [
        ..._filterServiceTypeProductsByPayment(dayServiceTypes.services, EPricing_ProductPayment.BYGUESTANDDAY),
        ..._filterServiceTypeProductsByPayment(dayServiceTypes.services, EPricing_ProductPayment.BYROOMANDDAY),
        ..._filterServiceTypeProductsByPayment(dayServiceTypes.services, EPricing_ProductPayment.BYCOUNT),
        ..._filterServiceTypeProductsByPayment(dayServiceTypes.services, EPricing_ProductPayment.BYGUEST),
      ];
      type RecurringProductItem = (typeof recurringProductsAll)[0];
      const recurringProductsRegular = [
        ..._filterServiceTypeProductsByPayment(regularServices, EPricing_ProductPayment.BYGUESTANDDAY),
        ..._filterServiceTypeProductsByPayment(regularServices, EPricing_ProductPayment.BYROOMANDDAY),
        ..._filterServiceTypeProductsByPayment(regularServices, EPricing_ProductPayment.BYCOUNT),
        ..._filterServiceTypeProductsByPayment(regularServices, EPricing_ProductPayment.BYGUEST),
      ];
      const recurringProductsNonRegular = [
        ..._filterServiceTypeProductsByPayment(nonRegularServices, EPricing_ProductPayment.BYGUESTANDDAY),
        ..._filterServiceTypeProductsByPayment(nonRegularServices, EPricing_ProductPayment.BYROOMANDDAY),
        ..._filterServiceTypeProductsByPayment(nonRegularServices, EPricing_ProductPayment.BYCOUNT),
        ..._filterServiceTypeProductsByPayment(nonRegularServices, EPricing_ProductPayment.BYGUEST),
      ];

      // Track applied VP bundles and what still needs to be priced individually.
      let vpCombinations = { dzCombos: 0, ezCombos: 0 };
      let remainingCounts = {
        roomsEZ: dayServiceTypes.roomsCountEZ,
        roomsDZ: dayServiceTypes.roomsCountDZ,
        tagespauschale: 0,
        dinner: 0,
      };

      // VP requires a day package participant to be combinable.
      const ganztagsSelectionsCount = regularServices
        .filter(s =>
          s.serviceType.products.some(p => {
            const prod = resolveListerCatalogProduct(p.product as CatalogRef | null, allProductsDb);
            const bundle = resolveListerCatalogBundle(
              p.bundle as CatalogRef | null,
              allBundlesDb,
              s.serviceType.listerServiceTypeAssignment,
            );
            return (
              prod?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE ||
              bundle?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE
            );
          }),
        )
        .reduce((sum, s) => sum + s.guestsCount, 0);

      // Rooms are the second hard limit for VP combinations.
      const roomPersonsEZ = dayServiceTypes.roomsCountEZ;
      const roomPersonsDZ = dayServiceTypes.roomsCountDZ;
      const totalRoomPersons = roomPersonsEZ + roomPersonsDZ;

      if (isSeminarDay && isFullDay && ganztagsSelectionsCount > 0 && totalRoomPersons > 0) {
        // Count dinner from addons
        let dinnerFromAddons = 0;
        if (day && day.addons) {
          dinnerFromAddons = day.addons
            .filter(addon => {
              const addonProduct = allProductsDb.find(p => p.sku === addon.sku);
              return addonProduct && addonProduct.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER;
            })
            .reduce((sum, addon) => sum + (addon.count || 1), 0);
        }

        // Also count dinner from recurring products (service types that include dinner)
        const dinnerFromRecurring = recurringProductsRegular
          .filter(
            p =>
              (p.bundle?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER ||
                p.product?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER) &&
              (isFullDay ? p.isForDayVisitor : p.isForSemidayVisitor),
          )
          .reduce((sum, p) => sum + p.guestsCount, 0);

        const totalDinnerCount = dinnerFromAddons + dinnerFromRecurring;

        // VP count = min(Ganztags selections, room persons, dinner count)
        if (totalDinnerCount > 0) {
          const vpCount = Math.min(ganztagsSelectionsCount, totalRoomPersons, totalDinnerCount);

          // Get VP prices to determine which variant is cheaper (prefer priced hierarchy twin).
          const vpPriceParams = {
            date: dayMom.toDate(),
            guestCount: dayServiceTypes.totalGuests,
            itemCount: vpCount,
            priceListId: priceList.id,
            isPremium: input.isPremium,
            publicCompanyId: input.publicCompanyId,
          };
          const vpEzProduct = this.findPricedProductByLookupCode(EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ, vpPriceParams);
          const vpDzProduct = this.findPricedProductByLookupCode(EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ, vpPriceParams);

          let vpEzPrice = Infinity;
          let vpDzPrice = Infinity;

          if (vpEzProduct) {
            const price = this.priceSelector?.findPrice({
              priceListId: priceList.id,
              productId: vpEzProduct.id,
              guestCount: dayServiceTypes.totalGuests,
              itemCount: vpCount,
              date: dayMom.toDate(),
              isPremium: input.isPremium,
              publicCompanyId: input.publicCompanyId,
            });
            if (price && price.price !== null && price.price !== NO_PRICE) {
              vpEzPrice = price.price;
            }
          }

          if (vpDzProduct) {
            const price = this.priceSelector?.findPrice({
              priceListId: priceList.id,
              productId: vpDzProduct.id,
              guestCount: dayServiceTypes.totalGuests,
              itemCount: vpCount,
              date: dayMom.toDate(),
              isPremium: input.isPremium,
              publicCompanyId: input.publicCompanyId,
            });
            if (price && price.price !== null && price.price !== NO_PRICE) {
              vpDzPrice = price.price;
            }
          }

          // Distribute VP to cheaper variant first
          if (vpEzPrice <= vpDzPrice) {
            // EZ is cheaper or equal, prioritize EZ
            vpCombinations.ezCombos = Math.min(roomPersonsEZ, vpCount);
            vpCombinations.dzCombos = Math.min(roomPersonsDZ, vpCount - vpCombinations.ezCombos);
          } else {
            // DZ is cheaper, prioritize DZ
            vpCombinations.dzCombos = Math.min(roomPersonsDZ, vpCount);
            vpCombinations.ezCombos = Math.min(roomPersonsEZ, vpCount - vpCombinations.dzCombos);
          }

          // Update remaining counts
          remainingCounts.roomsEZ = roomPersonsEZ - vpCombinations.ezCombos;
          remainingCounts.roomsDZ = roomPersonsDZ - vpCombinations.dzCombos;
          remainingCounts.tagespauschale = ganztagsSelectionsCount - vpCombinations.dzCombos - vpCombinations.ezCombos;
          remainingCounts.dinner = totalDinnerCount - vpCombinations.dzCombos - vpCombinations.ezCombos;
        } else {
          // No dinner, no VP combinations possible
          remainingCounts.tagespauschale = ganztagsSelectionsCount;
          remainingCounts.dinner = 0;
        }
      }

      // Centralized add helper keeps merge semantics identical for bundle/product/facility items.
      const _addItem = (
        item: { bundleId?: number; productId?: number; facilityId?: number },
        itemCount: number,
        merge: 'ADD' | 'IGNORE' | 'NEW' = 'ADD',
      ) => {
        if (item.bundleId) {
          const lineItem = this.getBundleLineItem(
            dayNum,
            {
              priceListId: priceList.id,
              bundleId: item.bundleId,
              guestCount: dayServiceTypes.totalGuests,
              itemCount: itemCount,
              date: dayMom.toDate(),
              isPremium: input.isPremium,
              publicCompanyId: input.publicCompanyId,
            },
            null,
            calcOptions,
          );
          if (lineItem) mergeLineItem(result, lineItem, merge);
        }
        if (item.productId) {
          const lineItem = this.getProductLineItem(
            dayNum,
            {
              priceListId: priceList.id,
              productId: item.productId,
              guestCount: dayServiceTypes.totalGuests,
              itemCount: itemCount,
              date: dayMom.toDate(),
              isPremium: input.isPremium,
              publicCompanyId: input.publicCompanyId,
            },
            null,
            calcOptions,
          );
          if (lineItem) mergeLineItem(result, lineItem, merge);
        }
        if (item.facilityId) {
          const lineItem = this.getFacilityLineItem(
            dayNum,
            {
              priceListId: priceList.id,
              facilityId: item.facilityId,
              guestCount: dayServiceTypes.totalGuests,
              itemCount: itemCount,
              date: dayMom.toDate(),
              isPremium: input.isPremium,
              publicCompanyId: input.publicCompanyId,
            },
            null,
            calcOptions,
          );
          if (lineItem) mergeLineItem(result, lineItem, merge);
        }
      };

      if (!addonsOnly) {
        const _isRoom = (lookupCode?: string | null) =>
          lookupCode &&
          (lookupCode === EPricing_ProductLookupCode.ROOM_EZ ||
            lookupCode === EPricing_ProductLookupCode.ROOM_DZ ||
            lookupCode === EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ ||
            lookupCode === EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ);

        const _isVollpensionRoom = (lookupCode?: string | null) =>
          lookupCode &&
          (lookupCode === EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ || lookupCode === EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ);

        // Executes pricing for one day-role (prevday, seminar day, departure).
        const _doDay = (ftProducts: (r: RecurringProductItem) => boolean, ftRooms: (r: RecurringProductItem) => boolean) => {
          // Check if combinations are actually active
          let combosActive = vpCombinations.dzCombos > 0 || vpCombinations.ezCombos > 0;

          // Combinations are only valid if dedicated VP SKUs can be priced on this day.
          if (combosActive) {
            const vpComboParams = {
              date: dayMom.toDate(),
              guestCount: dayServiceTypes.totalGuests,
              itemCount: Math.max(vpCombinations.ezCombos, vpCombinations.dzCombos, 1),
              priceListId: priceList.id,
              isPremium: input.isPremium,
              publicCompanyId: input.publicCompanyId,
            };
            const vpEzProduct = this.findPricedProductByLookupCode(EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ, vpComboParams);
            const vpDzProduct = this.findPricedProductByLookupCode(EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ, vpComboParams);

            // Check if prices exist for VP products
            let hasVpEzPrice = false;
            let hasVpDzPrice = false;

            if (vpEzProduct && vpCombinations.ezCombos > 0) {
              const vpEzPrice = this.priceSelector?.findPrice({
                priceListId: priceList.id,
                productId: vpEzProduct.id,
                guestCount: dayServiceTypes.totalGuests,
                itemCount: vpCombinations.ezCombos,
                date: dayMom.toDate(),
                isPremium: input.isPremium,
                publicCompanyId: input.publicCompanyId,
              });
              hasVpEzPrice = !!vpEzPrice && vpEzPrice.price !== null && vpEzPrice.price !== NO_PRICE;
            }

            if (vpDzProduct && vpCombinations.dzCombos > 0) {
              const vpDzPrice = this.priceSelector?.findPrice({
                priceListId: priceList.id,
                productId: vpDzProduct.id,
                guestCount: dayServiceTypes.totalGuests,
                itemCount: vpCombinations.dzCombos,
                date: dayMom.toDate(),
                isPremium: input.isPremium,
                publicCompanyId: input.publicCompanyId,
              });
              hasVpDzPrice = !!vpDzPrice && vpDzPrice.price !== null && vpDzPrice.price !== NO_PRICE;
            }

            // If no VP prices are available, deactivate combinations
            if ((vpCombinations.ezCombos > 0 && !hasVpEzPrice) || (vpCombinations.dzCombos > 0 && !hasVpDzPrice)) {
              combosActive = false;
              vpCombinations.ezCombos = 0;
              vpCombinations.dzCombos = 0;
              // Fall back to explicit room/day-package/dinner items when VP cannot be priced.
              remainingCounts.roomsEZ = dayServiceTypes.roomsCountEZ;
              remainingCounts.roomsDZ = dayServiceTypes.roomsCountDZ;
              remainingCounts.tagespauschale = recurringProductsRegular
                .filter(
                  p =>
                    p.guestsCount > 0 &&
                    (p.bundle?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE ||
                      p.product?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE) &&
                    (isFullDay ? p.isForDayVisitor : p.isForSemidayVisitor),
                )
                .reduce((sum, p) => sum + p.guestsCount, 0);

              // Recalculate dinner count
              let dinnerFromAddons = 0;
              if (day && day.addons) {
                dinnerFromAddons = day.addons
                  .filter(addon => {
                    const addonProduct = allProductsDb.find(p => p.sku === addon.sku);
                    return addonProduct && addonProduct.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER;
                  })
                  .reduce((sum, addon) => sum + (addon.count || 1), 0);
              }

              const dinnerFromRecurring = recurringProductsRegular
                .filter(
                  p =>
                    p.guestsCount > 0 &&
                    (p.bundle?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER ||
                      p.product?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER) &&
                    (isFullDay ? p.isForDayVisitor : p.isForSemidayVisitor),
                )
                .reduce((sum, p) => sum + p.guestsCount, 0);

              remainingCounts.dinner = dinnerFromAddons + dinnerFromRecurring;
            }
          }

          // Write VP items first so subsequent product filters can skip already-covered parts.
          if (combosActive) {
            const vpAddParams = {
              date: dayMom.toDate(),
              guestCount: dayServiceTypes.totalGuests,
              itemCount: Math.max(vpCombinations.ezCombos, vpCombinations.dzCombos, 1),
              priceListId: priceList.id,
              isPremium: input.isPremium,
              publicCompanyId: input.publicCompanyId,
            };
            const vpEzProduct = this.findPricedProductByLookupCode(EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ, vpAddParams);
            const vpDzProduct = this.findPricedProductByLookupCode(EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ, vpAddParams);

            // Add DZ Vollpension combinations
            if (vpCombinations.dzCombos > 0) {
              if (vpDzProduct) {
                _addItem({ productId: vpDzProduct.id }, vpCombinations.dzCombos, 'ADD');
              } else {
                // Fallback: add individual components for DZ combinations
                console.warn('VOLLPENSIONSPAUSCHALE_DZ product not found, falling back to individual components');
                const roomDzRecurring =
                  _getForCode(
                    recurringProductsAll.filter(p => ftRooms(p)),
                    EPricing_ProductLookupCode.ROOM_DZ,
                  ) ||
                  _getForCode(
                    recurringProductsAll.filter(p => p.isForRoom),
                    EPricing_ProductLookupCode.ROOM_DZ,
                  );
                // Prefer TAGESPAUSCHALE from service types with higher guest counts
                const tagespauschaleProducts = recurringProductsRegular
                  .filter(
                    p =>
                      ftProducts(p) &&
                      (p.bundle?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE ||
                        p.product?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE),
                  )
                  .sort((a, b) => b.guestsCount - a.guestsCount);
                const tagespauschaleProduct = tagespauschaleProducts[0];

                // Prefer ADDON_DINNER from service types with higher guest counts
                const dinnerProducts = recurringProductsRegular
                  .filter(
                    p =>
                      ftProducts(p) &&
                      (p.bundle?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER ||
                        p.product?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER),
                  )
                  .sort((a, b) => b.guestsCount - a.guestsCount);
                const dinnerProduct = dinnerProducts[0];

                if (roomDzRecurring?.bundle) _addItem({ bundleId: roomDzRecurring.bundle.id }, vpCombinations.dzCombos, 'ADD');
                else if (roomDzRecurring?.product) _addItem({ productId: roomDzRecurring.product.id }, vpCombinations.dzCombos, 'ADD');

                if (tagespauschaleProduct?.bundle) _addItem({ bundleId: tagespauschaleProduct.bundle.id }, vpCombinations.dzCombos, 'ADD');
                else if (tagespauschaleProduct?.product) _addItem({ productId: tagespauschaleProduct.product.id }, vpCombinations.dzCombos, 'ADD');

                if (dinnerProduct?.bundle) _addItem({ bundleId: dinnerProduct.bundle.id }, vpCombinations.dzCombos, 'ADD');
                else if (dinnerProduct?.product) _addItem({ productId: dinnerProduct.product.id }, vpCombinations.dzCombos, 'ADD');
              }
            }

            // Add EZ Vollpension combinations
            if (vpCombinations.ezCombos > 0) {
              if (vpEzProduct) {
                _addItem({ productId: vpEzProduct.id }, vpCombinations.ezCombos, 'ADD');
              } else {
                // Fallback: add individual components for EZ combinations
                console.warn('VOLLPENSIONSPAUSCHALE_EZ product not found, falling back to individual components');
                const roomEzRecurring =
                  _getForCode(
                    recurringProductsAll.filter(p => ftRooms(p)),
                    EPricing_ProductLookupCode.ROOM_EZ,
                  ) ||
                  _getForCode(
                    recurringProductsAll.filter(p => p.isForRoom),
                    EPricing_ProductLookupCode.ROOM_EZ,
                  );
                // Prefer TAGESPAUSCHALE from service types with higher guest counts
                const tagespauschaleProducts = recurringProductsRegular
                  .filter(
                    p =>
                      ftProducts(p) &&
                      (p.bundle?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE ||
                        p.product?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE),
                  )
                  .sort((a, b) => b.guestsCount - a.guestsCount);
                const tagespauschaleProduct = tagespauschaleProducts[0];

                // Prefer ADDON_DINNER from service types with higher guest counts
                const dinnerProducts = recurringProductsRegular
                  .filter(
                    p =>
                      ftProducts(p) &&
                      (p.bundle?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER ||
                        p.product?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER),
                  )
                  .sort((a, b) => b.guestsCount - a.guestsCount);
                const dinnerProduct = dinnerProducts[0];

                if (roomEzRecurring?.bundle) _addItem({ bundleId: roomEzRecurring.bundle.id }, vpCombinations.ezCombos, 'ADD');
                else if (roomEzRecurring?.product) _addItem({ productId: roomEzRecurring.product.id }, vpCombinations.ezCombos, 'ADD');

                if (tagespauschaleProduct?.bundle) _addItem({ bundleId: tagespauschaleProduct.bundle.id }, vpCombinations.ezCombos, 'ADD');
                else if (tagespauschaleProduct?.product) _addItem({ productId: tagespauschaleProduct.product.id }, vpCombinations.ezCombos, 'ADD');

                if (dinnerProduct?.bundle) _addItem({ bundleId: dinnerProduct.bundle.id }, vpCombinations.ezCombos, 'ADD');
                else if (dinnerProduct?.product) _addItem({ productId: dinnerProduct.product.id }, vpCombinations.ezCombos, 'ADD');
              }
            }
          }

          // For REGULAR services, skip parts already represented by VP bundles.
          // Non-REGULAR service types are always priced explicitly.
          const productsRegularFiltered = recurringProductsRegular.filter(
            p =>
              p.guestsCount > 0 &&
              ftProducts(p) &&
              !_isRoom(p.bundle?.lookupCode || p.product?.lookupCode) &&
              !(
                combosActive &&
                (p.bundle?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE ||
                  p.product?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE ||
                  p.bundle?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER ||
                  p.product?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER)
              ),
          );
          const productsNonRegular = recurringProductsNonRegular.filter(
            p => p.guestsCount > 0 && ftProducts(p) && !_isRoom(p.bundle?.lookupCode || p.product?.lookupCode),
          );
          const products = [...productsRegularFiltered, ...productsNonRegular];

          for (const p of products) {
            if (p.bundle) _addItem({ bundleId: p.bundle.id }, p.guestsCount, p.recurring !== EPricing_ProductPayment.BYCOUNT ? 'ADD' : 'IGNORE');
            else if (p.product)
              _addItem({ productId: p.product.id }, p.guestsCount, p.recurring !== EPricing_ProductPayment.BYCOUNT ? 'ADD' : 'IGNORE');
          }

          // Price leftover day packages that were not consumed by VP combinations.
          if (combosActive && remainingCounts.tagespauschale > 0) {
            // Prefer TAGESPAUSCHALE from service types with higher guest counts
            const tagespauschaleProducts = recurringProductsRegular
              .filter(
                p =>
                  ftProducts(p) &&
                  (p.bundle?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE ||
                    p.product?.lookupCode === EPricing_ProductLookupCode.TAGESPAUSCHALE),
              )
              .sort((a, b) => b.guestsCount - a.guestsCount); // Sort by guest count descending

            const tagespauschaleProduct = tagespauschaleProducts[0];
            if (tagespauschaleProduct?.bundle) _addItem({ bundleId: tagespauschaleProduct.bundle.id }, remainingCounts.tagespauschale, 'ADD');
            else if (tagespauschaleProduct?.product) _addItem({ productId: tagespauschaleProduct.product.id }, remainingCounts.tagespauschale, 'ADD');
          }

          // Price leftover dinner portions not consumed by VP combinations.
          if (combosActive && remainingCounts.dinner > 0) {
            // Prefer ADDON_DINNER from service types with higher guest counts
            const dinnerProductsRecurring = recurringProductsRegular
              .filter(
                p =>
                  ftProducts(p) &&
                  (p.bundle?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER ||
                    p.product?.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER),
              )
              .sort((a, b) => b.guestsCount - a.guestsCount); // Sort by guest count descending

            const dinnerProductRecurring = dinnerProductsRecurring[0];
            if (dinnerProductRecurring?.bundle) _addItem({ bundleId: dinnerProductRecurring.bundle.id }, remainingCounts.dinner, 'ADD');
            else if (dinnerProductRecurring?.product) _addItem({ productId: dinnerProductRecurring.product.id }, remainingCounts.dinner, 'ADD');
            else {
              // Dinner may exist only as addon; fallback to lookup-based product resolution.
              const fallbackDinner = allProductsDb.find(p => p.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER);
              if (fallbackDinner) _addItem({ productId: fallbackDinner.id }, remainingCounts.dinner, 'ADD');
            }
            remainingCounts.dinner = 0; // Mark as processed
          }

          // Rooms are mandatory price components unless already covered by VP combinations.
          const roomEzRecurring =
            _getForCode(
              recurringProductsAll.filter(p => ftRooms(p)),
              EPricing_ProductLookupCode.ROOM_EZ,
            ) ||
            _getForCode(
              recurringProductsAll.filter(p => p.isForRoom),
              EPricing_ProductLookupCode.ROOM_EZ,
            );
          const roomDzRecurring =
            _getForCode(
              recurringProductsAll.filter(p => ftRooms(p)),
              EPricing_ProductLookupCode.ROOM_DZ,
            ) ||
            _getForCode(
              recurringProductsAll.filter(p => p.isForRoom),
              EPricing_ProductLookupCode.ROOM_DZ,
            );

          // Service type configuration may omit room SKUs; use catalog fallback.
          const roomEzProduct = roomEzRecurring?.product || allProductsDb.find(p => p.lookupCode === EPricing_ProductLookupCode.ROOM_EZ);
          const roomEzBundle = roomEzRecurring?.bundle || allBundlesDb.find(b => b.lookupCode === EPricing_ProductLookupCode.ROOM_EZ);
          const roomDzProduct = roomDzRecurring?.product || allProductsDb.find(p => p.lookupCode === EPricing_ProductLookupCode.ROOM_DZ);
          const roomDzBundle = roomDzRecurring?.bundle || allBundlesDb.find(b => b.lookupCode === EPricing_ProductLookupCode.ROOM_DZ);

          if (remainingCounts.roomsEZ > 0) {
            if (roomEzBundle) _addItem({ bundleId: roomEzBundle.id }, remainingCounts.roomsEZ, 'ADD');
            else if (roomEzProduct) _addItem({ productId: roomEzProduct.id }, remainingCounts.roomsEZ, 'ADD');
          }
          if (remainingCounts.roomsDZ > 0) {
            if (roomDzBundle) _addItem({ bundleId: roomDzBundle.id }, remainingCounts.roomsDZ, 'ADD');
            else if (roomDzProduct) _addItem({ productId: roomDzProduct.id }, remainingCounts.roomsDZ, 'ADD');
            // Fallback: book as single rooms if no double rooms available
            else if (roomEzBundle) _addItem({ bundleId: roomEzBundle.id }, remainingCounts.roomsDZ, 'ADD');
            else if (roomEzProduct) _addItem({ productId: roomEzProduct.id }, remainingCounts.roomsDZ, 'ADD');
          }
        };

        if (isPrevDay)
          _doDay(
            r => r.isForPrevday,
            r => r.isForPrevday,
          );
        else if (isSeminarDay)
          _doDay(
            r => (isFullDay ? r.isForDayVisitor : r.isForSemidayVisitor),
            r => (isFullDay ? r.isForOvernight : r.isForSemidayOvernight),
          );
        else if (isDepartureDay)
          _doDay(
            r => (isFullDay ? r.isForDeparture : r.isForSemidayDeparture),
            r => r.isForNextday,
          );
      }

      if (day && day.addons && day.addons.length > 0) {
        for (const addon of day.addons) {
          const addonProduct = allProductsDb.find(p => p.sku === addon.sku);
          if (addonProduct) {
            // Handle ADDON_DINNER - skip when combos are active (already handled above), otherwise process normally
            if (addonProduct.lookupCode === EPricing_ProductLookupCode.ADDON_DINNER) {
              const combosActive = vpCombinations.dzCombos > 0 || vpCombinations.ezCombos > 0;
              if (!combosActive) {
                addon.sku = addonProduct.sku;
                _addItem({ productId: addonProduct.id }, addon.count || 1, 'ADD');
              }
            } else {
              // Regular addon processing
              addon.sku = addonProduct.sku;
              _addItem({ productId: addonProduct.id }, addon.count || 1, 'ADD');
            }
          }
        }
      }

      if (dayNum >= 0) {
        // Check if we will select a specific meeting room (moved outside addonsOnly block to be accessible for addon logic)
        let specificRoomSelected = false;
        if (day && day.occupancy && day.occupancy.length > 0) {
          const availableFacilities = allFacilitiesDb.filter(f => f.meetingRoom && f.meetingRoom.hotelId === this.hotel!.id);
          if (availableFacilities.length > 0) {
            let maxRequiredGuests = 0;
            let primarySeating = 'UFORM';
            for (let i = 0; i < day.occupancy.length; i++) {
              const guestCount = day.seatCount?.[i] || dayServiceTypes.totalGuests;
              const seatingType = day.seating?.[i] || 'UFORM';
              if (guestCount > maxRequiredGuests) {
                maxRequiredGuests = guestCount;
                primarySeating = seatingType;
              }
            }
            if (maxRequiredGuests > 0) {
              const selectedRoom = selectOptimalMeetingRoom(availableFacilities, maxRequiredGuests, primarySeating);
              specificRoomSelected = !!selectedRoom;
            }
          }
        }

        if (!addonsOnly) {
          const recurringFacilities = _filterServiceTypeFacilitiesByPayment(dayServiceTypes.services, EPricing_FacilityPayment.BYDAY);

          const byDayFacilities = recurringFacilities.filter(p => (isFullDay ? p.isForDay : p.isForSemiday));

          for (const f of byDayFacilities) {
            // Check if this is a seminar room facility (generic or specific meeting room linked to this hotel)
            const isSeminarRoomFacility =
              f.facility.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_FULLDAY ||
              f.facility.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_SEMIDAY ||
              (f.facility.meetingRoom && f.facility.meetingRoom.hotelId === this.hotel!.id);

            if (isSeminarRoomFacility) {
              // Only add seminar room facilities if no specific room was selected
              // (specific rooms are added in the automatic meeting room selection below)
              if (!specificRoomSelected) {
                for (let i = 0; i < f.includedCount; i++) {
                  const lineItem: ICalcLineItem = {
                    day: dayNum,
                    sku: f.facility.sku,
                    name: f.facility.name,
                    facility: f.facility,
                    count: 1,
                    isIncluded: true,
                    priceItem: 0,
                    priceGross: 0,
                    priceNet: 0,
                    components: [],
                    taxes: [],
                  };
                  mergeLineItem(result, lineItem, 'NEW');
                }
              }
            } else {
              const lineItem: ICalcLineItem = {
                day: dayNum,
                sku: f.facility.sku,
                name: f.facility.name,
                facility: f.facility,
                count: f.includedCount,
                isIncluded: true,
                priceItem: 0,
                priceGross: 0,
                priceNet: 0,
                components: [],
                taxes: [],
              };
              mergeLineItem(result, lineItem, 'IGNORE');
            }
          }

          // Automatic meeting room selection for epilo bookings.
          if (day && day.occupancy && day.occupancy.length > 0) {
            // Restrict to rooms that belong to the current hotel.
            const availableFacilities = allFacilitiesDb.filter(f => f.meetingRoom && f.meetingRoom.hotelId === this.hotel!.id);

            // Keep selected rooms unique per day and preserve display metadata.
            const selectedRoomsForDay: {
              facilityId: number;
              seatingType: string;
              seatCount: number;
              equipment: (typeof day.occupancy)[number];
            }[] = [];

            for (let roomIndex = 0; roomIndex < day.occupancy.length; roomIndex++) {
              const guestCount = day.seatCount?.[roomIndex] || dayServiceTypes.totalGuests;
              const seatingType = day.seating?.[roomIndex] || 'UFORM';
              const equipment = day.occupancy[roomIndex];

              if (guestCount > 0) {
                const remainingFacilities = availableFacilities.filter(f => !selectedRoomsForDay.some(r => r.facilityId === f.id));

                if (remainingFacilities.length > 0) {
                  const selectedRoom = selectOptimalMeetingRoom(remainingFacilities, guestCount, seatingType);

                  if (selectedRoom) {
                    selectedRoomsForDay.push({ facilityId: selectedRoom.id, seatingType, seatCount: guestCount, equipment });
                  }
                } else {
                  const minimumSelectedRoom = selectOptimalMeetingRoom(availableFacilities, guestCount, seatingType);
                  if (minimumSelectedRoom) {
                    selectedRoomsForDay.push({ facilityId: minimumSelectedRoom.id, seatingType, seatCount: guestCount, equipment });
                  }
                }
              }
            }

            for (let i = 0; i < selectedRoomsForDay.length; i++) {
              const { facilityId, seatingType, seatCount, equipment } = selectedRoomsForDay[i];
              const isMainRoom = !mainRoomAddedPerDay[dayNum] && i === 0;

              const lineItem = this.getFacilityLineItem(
                dayNum,
                {
                  priceListId: priceList.id,
                  facilityId: facilityId,
                  guestCount: dayServiceTypes.totalGuests,
                  itemCount: 1,
                  date: dayMom.toDate(),
                  isPremium: input.isPremium,
                  publicCompanyId: input.publicCompanyId,
                },
                null,
                calcOptions,
              );

              // Selector value has priority; input/calculator setting is only fallback.
              const selectorIncludeMainRoom = lineItem?.selector?.includeMainRoomInPackage;
              const effectiveIncludeMainRoom = selectorIncludeMainRoom !== undefined
                ? selectorIncludeMainRoom
                : includeMainRoomInPackage;
              const shouldBeIncludedInPackage = isMainRoom && effectiveIncludeMainRoom === true;

              if (shouldBeIncludedInPackage) {
                mainRoomAddedPerDay[dayNum] = true;

                if (lineItem) {
                  // Keep line item visible but price-neutral when main room is package-included.
                  lineItem.priceItem = 0;
                  lineItem.priceGross = 0;
                  lineItem.priceNet = 0;
                  lineItem.isIncluded = true;
                  lineItem.components = lineItem.components.map(c => ({ ...c, price: 0 }));
                  lineItem.taxes = lineItem.taxes.map(t => ({ ...t, price: 0 }));
                  lineItem.seatingType = seatingType;
                  lineItem.seatCount = seatCount;
                  lineItem.seminarRoomEquipment = equipment;
                  mergeLineItem(result, lineItem, 'NEW');
                }
              } else {
                if (isMainRoom) {
                  mainRoomAddedPerDay[dayNum] = true;
                }
                if (lineItem) {
                  lineItem.seatingType = seatingType;
                  lineItem.seatCount = seatCount;
                  lineItem.seminarRoomEquipment = equipment;
                  mergeLineItem(result, lineItem, 'NEW');
                }
              }
            }
          }
        }
        for (const addon of [...(day?.addonFacilities || [])]) {
          let addonFacility = addon.sku
            ? allFacilitiesDb.find(f => f.sku === addon.sku)
            : addon.lookupCode
              ? allFacilitiesDb.find(f => f.lookupCode === addon.lookupCode)
              : null;
          if (
            addonFacility &&
            (addonFacility.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_FULLDAY ||
              addonFacility.lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_SEMIDAY) &&
            !(addonFacility.meetingRoom && addonFacility.meetingRoom.hotelId === this.hotel!.id)
          ) {
            addonFacility = null;
          }
          if (addonFacility) {
            addon.sku = addonFacility.sku;
            if (addonFacility.recurring === EPricing_FacilityPayment.BYGUEST) {
              _addItem({ facilityId: addonFacility.id }, (addon.count || 1) * dayServiceTypes.totalGuests, 'ADD');
            } else if (addonFacility.recurring === EPricing_FacilityPayment.BYDAY || addonFacility.recurring === EPricing_FacilityPayment.BYCOUNT) {
              _addItem({ facilityId: addonFacility.id }, addon.count || 1, 'IGNORE');
            }
          }
        }

        const dailyFacilities = _filterServiceTypeFacilitiesByPayment(dayServiceTypes.services);

        const seminarroomFacility = _getForCode(
          dailyFacilities.filter(f => f.isForDay),
          EPricing_FacilityLookupCode.SEMINARROOM_FULLDAY,
        );
        const seminarroomSemidayFacility = _getForCode(
          dailyFacilities.filter(f => f.isForSemiday),
          EPricing_FacilityLookupCode.SEMINARROOM_SEMIDAY,
        );
        const addonSeminarroomFacility = _getAddonForCode(EPricing_FacilityLookupCode.SEMINARROOM_FULLDAY);
        const addonSeminarroomSemidayFacility = _getAddonForCode(EPricing_FacilityLookupCode.SEMINARROOM_SEMIDAY);

        // Only add addon seminar rooms if NO specific room was selected via automatic meeting room selection
        // (the automatic selection already handles room allocation based on occupancy)
        if ((day?.occupancy || []).length > 0 && !specificRoomSelected) {
          const bookedFullCount = (day?.occupancy || []).filter(o => isOccupancyFullDay(o)).length;
          const includedFullCount = (seminarroomFacility && seminarroomFacility.includedCount) || 0;
          const addonFullCount = Math.max(0, bookedFullCount - includedFullCount);
          if (addonSeminarroomFacility && addonSeminarroomFacility.facility && addonFullCount > 0) {
            for (let i = 0; i < addonFullCount; i++) {
              _addItem({ facilityId: addonSeminarroomFacility.facility!.id }, 1, 'NEW');
            }
          }
          const remainingFullCountIncluded = Math.max(0, includedFullCount - bookedFullCount);

          const bookedSemiCount = (day?.occupancy || []).filter(o => !isOccupancyFullDay(o)).length;
          const includedSemiCount = (seminarroomSemidayFacility && seminarroomSemidayFacility.includedCount) || 0;
          const addonSemiCount = Math.max(0, bookedSemiCount - includedSemiCount - remainingFullCountIncluded);
          if (addonSeminarroomSemidayFacility && addonSeminarroomSemidayFacility.facility && addonSemiCount > 0) {
            for (let i = 0; i < addonSemiCount; i++) {
              _addItem({ facilityId: addonSeminarroomSemidayFacility.facility!.id }, 1, 'NEW');
            }
          }
        }
      }
    }

    sumTotalPrices(result);
    result.lineItems.sort((a, b) => (a.day === null ? -1 : b.day === null ? 1 : a.day - b.day));
    attachOtherSeatingDescriptions(result, input);

    return result;
  }

  seatingLabels: Record<string, { de: string; en: string }> = {
    UFORM: { de: 'U-Form', en: 'U-Shape' },
    THEATER: { de: 'Theater', en: 'Theater' },
    PARLAMENT: { de: 'Parlament', en: 'Parliament' },
    CIRCLE: { de: 'Kreis', en: 'Circle' },
    BANKETT: { de: 'Bankett', en: 'Banquet' },
    COCKTAIL: { de: 'Cocktail', en: 'Cocktail' },
    BLOCK: { de: 'Block', en: 'Block' },
    OTHER: { de: 'Sonstige', en: 'Other' },
  };

  public getLineItemContent(calcOutput: ICalcPriceOutput, lineItem: ICalcLineItem, language: string) {
    const baseContent = super.getLineItemContent(calcOutput, lineItem, language);

    if (!lineItem.facility || !isSeminarRoomFacility(lineItem.facility)) return baseContent;

    const isEnglish = (language || '').toUpperCase().startsWith('EN');
    const seminarRoomLabel = getSeminarRoomDisplayHeader(language);
    let details = baseContent.details || '';

    // Add seating type and participant count if available
    if (lineItem.seatingType && lineItem.seatCount) {
      const seatingLabel = this.seatingLabels[lineItem.seatingType]?.[isEnglish ? 'en' : 'de'] || lineItem.seatingType;
      const participantsText = isEnglish 
        ? `${seatingLabel}, ${lineItem.seatCount} participants`
        : `${seatingLabel}, ${lineItem.seatCount} Teilnehmer`;
      details = details ? `${details}, ${participantsText}` : participantsText;
    }

    const sentence = formatSeminarRoomEquipmentSentence(lineItem.seminarRoomEquipment, language);
    const updatedContent = {
      ...baseContent,
      header: seminarRoomLabel,
      details: replaceSeminarRoomEquipmentSentence(details, sentence),
    };

    if (lineItem.seatingType === 'OTHER' && lineItem.seatingDescription) {
      const seatingLabel = individualSeatingDetailLabel(language);
      updatedContent.details = updatedContent.details
        ? `${updatedContent.details}\n\n${seatingLabel}${lineItem.seatingDescription}`
        : `${seatingLabel}${lineItem.seatingDescription}`;
    }

    return updatedContent;
  }
}

export default QuickPriceCalculatorLister;
